import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { WARootState } from './store';

export interface YodleeToken {
  token: string;
}

// Part of the shared aggregator credentials contract, not Yodlee-specific.
// Re-exported here so existing importers keep working.
export { AggregatorFormStatus } from '@/utils/aggregatorCredentials';
import {
  AggregatorFormStatus,
  type AggregatorCredentialFormField,
} from '@/utils/aggregatorCredentials';

export interface AggregatorStatus {
  StatusCode: AggregatorFormStatus;
  Message?: string;
}

export interface AggregatorCredentialsForm {
  Type?: number;
  Status: AggregatorStatus;
  Instructions?: string;
  CredentialFormFields?: AggregatorCredentialFormField[] | null;
}

/**
 * Mirrors `AggregatorModel.cs`. `CredentialsForm` is optional here, unlike in the
 * generic credentials flow: the Yodlee endpoints answer with an envelope that may
 * carry no form at all.
 *
 * `AdditionalAccounts`, `Institution` and `Role` are deliberately absent — nothing
 * in the frontend reads them, and they still survive any spread of a response.
 */
export interface AggregatorModel {
  FinancialInstitutionId: number;
  AggregatorId: number;
  WealthId: number | null;
  WealthAccountId?: number | null;
  WealthStatus?: number | null;
  Receipt?: string | null;
  HasRealtime?: boolean;
  AdvisorInstitution?: boolean;
  GUID?: string;
  AuthToken?: string;
  CredentialsForm?: AggregatorCredentialsForm;
  Credentials?: AggregatorCredentialFormField[] | null;
  OauthURL?: string | null;
}

export interface CreateYodleeCredentialsRequest {
  FinancialInstitutionId: number;
  ProviderAccountId: number;
  AuthToken: string;
}

export interface UpdateYodleeCredentialsRequest extends CreateYodleeCredentialsRequest {
  WealthId: number;
}

export interface LoadYodleeAccountsRequest {
  wealthId: number;
  YodleeToken: string;
  IsUpdate?: boolean;
}

export interface YodleeErrorLogRequest {
  providerAccountId?: string | number;
  providerId?: string | number;
  reference?: string;
  status?: string;
}

export interface YodleeErrorLogResponse {
  success: boolean;
  errorId?: string | number;
}

export interface YodleeAccountStatusRequest {
  providerAccountId: string | number;
}

export interface YodleeAccountStatusResponse {
  status: string;
  providerAccountId: string | number;
}

export const yodleeApi = createApi({
  reducerPath: 'yodleeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGGSERVICES_URL ?? '',
    prepareHeaders: (headers, { getState }) => {
      const state: WARootState = getState() as WARootState;

      const token = state.appView.UserInfo?.Token;
      const currentRole = state.auth.currentRole;

      if (token) {
        headers.set('Authentication', token);
        if (currentRole) {
          headers.set('Currentrole', currentRole.toString().toLowerCase());
        } else {
          // Default to investor role if none is set
          headers.set('Currentrole', 'investor');
        }
      }

      headers.set('accept', 'application/json, text/javascript, */*; q=0.01');
      headers.set('content-type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['YodleeAccount', 'YodleeError'],
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses `void` for no-argument endpoints
    getYodleeToken: builder.query<YodleeToken, void>({
      query: () => ({
        url: `/Api/yodlee/Auth`,
        method: 'GET',
        params: {
          _: new Date().getTime(),
        },
      }),
    }),
    createYodleeCredentials: builder.mutation<
      AggregatorModel,
      CreateYodleeCredentialsRequest
    >({
      query: (body) => ({
        url: `/api/Yodlee/Credentials`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['YodleeAccount'],
    }),
    // Reconnecting an existing wealth must use this, not the POST above: the
    // create path leaves an existing wealth untouched, so its error status
    // survives the reconnect. This is the only call that resets it (EUD-797).
    updateYodleeCredentials: builder.mutation<
      AggregatorModel,
      UpdateYodleeCredentialsRequest
    >({
      query: (body) => ({
        url: `/api/Yodlee/Credentials`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['YodleeAccount'],
    }),
    loadYodleeAccounts: builder.mutation<
      AggregatorModel,
      LoadYodleeAccountsRequest
    >({
      query: ({ wealthId, YodleeToken, IsUpdate = false }) => ({
        url: `/api/Yodlee/Accounts/${String(wealthId)}`,
        method: 'GET',
        params: { YodleeToken, IsUpdate },
      }),
      invalidatesTags: ['YodleeAccount'],
    }),
    logYodleeError: builder.mutation<
      YodleeErrorLogResponse,
      YodleeErrorLogRequest
    >({
      queryFn: async (errorData, _api, __, baseQuery) => {
        try {
          const result = await baseQuery({
            url: '/Api/investor/yodleeErrorLogs',
            method: 'POST',
            body: errorData,
          });

          if (result.error) {
            console.error('Error logging yodlee error:', result.error);
            return { error: result.error };
          }

          return { data: result.data as YodleeErrorLogResponse };
        } catch (err) {
          console.error('Exception logging yodlee error:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: ['YodleeError'],
    }),
    checkYodleeAccountStatus: builder.query<
      YodleeAccountStatusResponse,
      YodleeAccountStatusRequest
    >({
      queryFn: async (statusRequest, _api, __, baseQuery) => {
        try {
          const result = await baseQuery({
            url: `/Api/yodlee/AccountStatus`,
            method: 'GET',
            params: statusRequest,
          });

          if (result.error) {
            console.error(
              'Error checking yodlee account status:',
              result.error,
            );
            return { error: result.error };
          }

          return { data: result.data as YodleeAccountStatusResponse };
        } catch (err) {
          console.error('Exception checking yodlee account status:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (_result, _error, { providerAccountId }) => [
        { type: 'YodleeAccount', id: providerAccountId },
      ],
    }),
  }),
});

export const {
  useGetYodleeTokenQuery,
  useLazyGetYodleeTokenQuery,
  useCreateYodleeCredentialsMutation,
  useUpdateYodleeCredentialsMutation,
  useLoadYodleeAccountsMutation,
  useLogYodleeErrorMutation,
  useCheckYodleeAccountStatusQuery,
  useLazyCheckYodleeAccountStatusQuery,
} = yodleeApi;
